import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import _isEqual from 'lodash-es/isEqual'

import * as common from '../../../../../common'
import type { Expression } from '../../expression'
import * as expressionNode from '../../node'
import type { FnHelper } from '../helper'
import { Key } from '../key'
import * as fnNode from '../node'

import { CreateTuple } from './create-tuple'
import { Equals } from './equals'
import { CommonArgs } from './utils'

// TODO: maybe this could be modified to also works for substring (i.e. in: [ 'abc', 'abcdef' ])

/**
 * Checks if the value from the first expression appears anywhere in the list of
 * values from the second expression. If it does, the result is `true`.
 * Otherwise, it is `false`.
 *
 * @param expressions An expression producing a value followed by an expression
 * producing a list
 * @example
 * {
 *   in: [
 *     1,
 *     { '[]': [ 1, 2, 3 ] }
 *   ]
 * }
 * // ->
 * true
 *
 * {
 *   in: [
 *     4,
 *     { '[]': [ 1, 2, 3 ] }
 *   ]
 * }
 * // ->
 * false
 */
export interface In<out T = any> {
  /** {@link In} */
  readonly [Key.In]: T extends boolean
    ? readonly [Expression<any>, Expression<readonly unknown[]>]
    : never
}
export namespace In {
  export const isIn: TypeChecker<In> = TypeChecker.Factory.lazy(() =>
    TypeChecker.Factory.object<In>({
      [Key.In]: CommonArgs.isTwoExpressions as TypeChecker<In[Key.In]>,
    }),
  ).usingAlias('Fn.In')
  export const assertIsIn: TypeAsserter<In> = TypeAsserter.Factory.from(isIn)

  /**
   * @see {@link Node}
   */
  export class Node<T extends boolean = boolean> extends fnNode.Node<
    T,
    Key.In,
    {
      readonly item: expressionNode.Node
      readonly list: expressionNode.Node<readonly unknown[]>
    }
  > {
    public readonly type = common.value.types.Intrinsic.Boolean

    constructor(arg: Node<T>['arg']) {
      super(Key.In, arg)
    }
  }
  export namespace Node {
    export const isNode: TypeChecker<Node> = TypeChecker.Factory.instance(Node)
    export const assertIsNode: TypeAsserter<Node> =
      TypeAsserter.Factory.from(isNode)
  }

  export const HELPER: FnHelper<In> = {
    createNode: (fn, context) => {
      const [itemExpression, listExpression] = fn[Key.In]
      const item = expressionNode.from(itemExpression, context)
      const list = expressionNode.from(listExpression, context)
      if (expressionNode.ForValue.isForValue(list)) {
        const result =
          // If the list is somehow not a list: result is `false`
          TypeChecker.isArray(list.value) &&
          // If it has no items: result is `false`
          list.value.length > 0 &&
          (expressionNode.ForValue.isForValue(item)
            ? // If item is also a value: result can be determined
              list.value.some(val => _isEqual(val, item.value))
            : // Otherwise: result cannot be determined
              undefined)

        if (result !== undefined) {
          // If result has been determined, return it
          return new expressionNode.ForValue(result)
        }
      }
      if (CreateTuple.Node.isNode(list) && list.arg.length === 1) {
        const otherItem = list.arg[0]
        return new Equals.Node(
          // NOTE: Equals only accepts one value node, and it has to be the
          // first item in the list. In this case, we know that at most one of
          // the nodes is a value node because if they were both value nodes,
          // that would have been caught in the logic above. However, we don't
          // know which, if either, is a value node, so we need to check. In the
          // case where neither are value nodes, we put item first just so that
          // the order matches the expected order, in case someone needs to
          // debug
          expressionNode.ForValue.isForValue(otherItem)
            ? [otherItem, item as Node]
            : [item, otherItem as Node],
        )
      }
      return new Node({ item, list })
    },
    expressionsInArgs: fn => fn[Key.In],
    typeChecker: isIn,
  }
}
